import { enDashboard } from '~/src/locales/en/dashboard';
import { enMetrics } from '~/src/locales/en/metrics';
import { enCommon } from '~/src/locales/en/common';
import { enDatetime } from '~/src/locales/en/datetime';
import { enBidders } from '~/src/locales/en/bidders';
import { useTranslation } from 'react-i18next';
import { enChannels } from '~/src/locales/en/channels';
import { enReporting } from '~/src/locales/en/reporting';
import { enBrandsDashboard } from '~/src/locales/en/brandsDashboard';
import { enBrandSafety } from '~/src/locales/en/brandSafety';
import { enDeals } from '~/src/locales/en/deals';

export const enTranslation = {
  translation: {
    // by view
    ...enBidders,
    ...enBrandsDashboard,
    ...enBrandSafety,
    ...enChannels,
    ...enDashboard,
    ...enReporting,
    // by common
    ...enCommon,
    ...enDeals,
    ...enDatetime,
    ...enMetrics,
  },
};

// do not duplicate below into other locales
type TKeyBidders = keyof typeof enBidders;
type TKeyBrandsDashboard = keyof typeof enBrandsDashboard;
type TKeyBrandSafety = keyof typeof enBrandSafety;
type TKeyChannels = keyof typeof enChannels;
type TKeyCommon = keyof typeof enCommon;
type TKeyDashboard = keyof typeof enDashboard;
type TKeyDeals = keyof typeof enDeals;
type TKeyDatetime = keyof typeof enDatetime;
type TKeyMetric = keyof typeof enMetrics;
type TKeyReporting = keyof typeof enReporting;

export type TKey =
  | TKeyBidders
  | TKeyBrandsDashboard
  | TKeyBrandSafety
  | TKeyChannels
  | TKeyCommon
  | TKeyDashboard
  | TKeyDeals
  | TKeyDatetime
  | TKeyMetric
  | TKeyReporting
  | 'counter'
  | '';

export interface ITProps {
  count?: number; // used for pluralization
  date?: string;
  id?: string | number;
  item?: string; // catch all for item names example: 'bidder', 'channel', etc.
  altItem?: string;
  label?: string;
  name?: string; // name of an item preferrably from sql
  number?: number; // integer not necessarily used for count
  altNumber?: number; // integer not necessarily used for count
  selection?: string; // selected option
  timestamp?: string;
  total?: number;
  text?: string;
  altText?: string;
  source?: string;
  target?: string;
}

interface IPublicaTranslation {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: TKey, props?: Record<string, any>) => string;
  i18n: ReturnType<typeof useTranslation>['i18n'];
}

export const usePublicaTranslation = (): IPublicaTranslation => {
  const { t: originalT, i18n, ready } = useTranslation();

  // Throw a promise to trigger Suspense if translations aren't ready
  if (!ready) {
    throw new Promise((resolve) => {
      const checkReady = (): void => {
        if (i18n.isInitialized && i18n.hasLoadedNamespace('translation')) {
          resolve(undefined);
        } else {
          setTimeout(checkReady, 50);
        }
      };
      checkReady();
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const t = (key: TKey, props?: Record<string, any>): string => {
    if (!key) {
      return '';
    }
    return originalT(key, { ...props, defaultValue: key });
  };

  return { t, i18n };
};
